import { useCallback, useEffect, useMemo, useState } from "react";
import { LayoutMenuPosition } from "../../types";
import { CurationItem } from "../../Api/types";
import { parseSmartListConfig } from "../../Api/services/content/fetchContent";
import {
    SmartListState,
    createInitialSmartListState,
    deriveSmartListOptions,
} from "./smartListState";

export type EditContentFormState = {
    normalizedContentType: string;
    title: string;
    setTitle: (value: string) => void;
    alias: string;
    setAlias: (value: string) => void;
    pageSlug: string;
    setPageSlug: (value: string) => void;
    layoutMenuPosition: LayoutMenuPosition;
    setLayoutMenuPosition: (value: LayoutMenuPosition) => void;
    position: string;
    setPosition: (value: string) => void;
    smartList: SmartListState & {
        setTemplateId: (value: string) => void;
        setAdapterId: (value: number | null) => void;
        setGenre: (value: string) => void;
        setLabels: (value: string[]) => void;
        setSortOption: (value: string) => void;
        requiresGenre: boolean;
        requiresLabels: boolean;
    };
};

export type EditContentFormControllers = {
    formState: EditContentFormState;
    latestItem: CurationItem;
    applyItemData: (source: CurationItem) => void;
    smartListState: SmartListState;
};

export const useEditContentFormState = (initialItem: CurationItem): EditContentFormControllers => {
    const [latestItem, setLatestItem] = useState(initialItem);

    const [title, setTitle] = useState("");
    const [alias, setAlias] = useState("");
    const [pageSlug, setPageSlug] = useState("");
    const [layoutMenuPosition, setLayoutMenuPosition] = useState<LayoutMenuPosition>("Header");
    const [position, setPosition] = useState("");
    const [smartListState, setSmartListState] = useState<SmartListState>(
        createInitialSmartListState(),
    );

    const normalizedContentType = useMemo(
        () => (latestItem.contentType ?? "").toLowerCase(),
        [latestItem.contentType],
    );

    const applyItemData = useCallback((source: CurationItem) => {
        setLatestItem(source);
        setTitle(source.title ?? "");
        setAlias(source.metadata?.default?.alias ?? "");
        setPageSlug(source.metadata?.default?.["page-slug"] ?? "");
        setLayoutMenuPosition(
            (source.metadata?.default?.["layout-menu-position"] as LayoutMenuPosition) ?? "Header",
        );
        setPosition(source.metadata?.en_US?.url ?? source.metadata?.default?.url ?? "");

        const smartListConfig = parseSmartListConfig(source);
        if (smartListConfig) {
            setSmartListState({
                templateId: smartListConfig.templateId,
                adapterId: smartListConfig.adapterId,
                sortOption: smartListConfig.sortOption,
                genre: smartListConfig.genre ?? "",
                labels: smartListConfig.labels ? smartListConfig.labels.slice(0, 1) : [],
            });
        } else {
            setSmartListState(createInitialSmartListState());
        }
    }, []);

    useEffect(() => {
        applyItemData(initialItem);
    }, [applyItemData, initialItem]);

    const smartListDefinition = deriveSmartListOptions(smartListState);

    const formState: EditContentFormState = {
        normalizedContentType,
        title,
        setTitle,
        alias,
        setAlias,
        pageSlug,
        setPageSlug,
        layoutMenuPosition,
        setLayoutMenuPosition,
        position,
        setPosition,
        smartList: {
            ...smartListState,
            setTemplateId: (value: string) =>
                setSmartListState((prev) => ({ ...prev, templateId: value })),
            setAdapterId: (value: number | null) =>
                setSmartListState((prev) => ({ ...prev, adapterId: value })),
            setGenre: (value: string) => setSmartListState((prev) => ({ ...prev, genre: value })),
            setLabels: (value: string[]) =>
                setSmartListState((prev) => ({ ...prev, labels: value })),
            setSortOption: (value: string) =>
                setSmartListState((prev) => ({ ...prev, sortOption: value })),
            requiresGenre: smartListDefinition.requiresGenre,
            requiresLabels: smartListDefinition.requiresLabels,
        },
    };

    return {
        formState,
        latestItem,
        applyItemData,
        smartListState,
    };
};
